'use client'

import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { openCheckout, type PaddleProduct } from '@/lib/paddle/checkout'
import { PoweredByPaddle } from '@/components/PoweredByPaddle'

// Order: Single → Power Pack (hero, middle) → Starter.
// Middle position uses standard decoy/anchoring pattern — the recommended
// option catches eye-center attention. Breaks strict ascending order on
// purpose to maximize conversion to the highest-value pack.
const PACKS: Array<{
  count: number
  price: string
  perAudit: string
  save: string
  hero: boolean
  label: string
  product: PaddleProduct
}> = [
  {
    count: 1,
    price: '$5',
    perAudit: '$5.00',
    save: '',
    hero: false,
    label: 'Single',
    product: 'pack-single',
  },
  {
    count: 10,
    price: '$40',
    perAudit: '$4.00',
    save: 'Save 20%',
    hero: true,
    label: 'Power Pack',
    product: 'pack-power',
  },
  {
    count: 3,
    price: '$12',
    perAudit: '$4.00',
    save: 'Save 20%',
    hero: false,
    label: 'Starter',
    product: 'pack-starter',
  },
]

/**
 * Shared "Pick your audit pack" modal.
 * Used on /platform#pricing AND /platform/account ("Buy more" CTA).
 * Keeps users in-context — no page navigation to buy.
 */
export function PackModal({ onClose }: { onClose: () => void }) {
  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center p-4'
      style={{ background: 'rgba(0,0,0,0.7)' }}
      onClick={onClose}
    >
      <div
        className='w-full max-w-2xl rounded-2xl border p-8 bg-card border-border'
        onClick={e => e.stopPropagation()}
      >
        <div className='flex items-start justify-between mb-6'>
          <div>
            <h3 className='text-xl font-bold text-foreground mb-1'>
              Pick your audit pack
            </h3>
            <p className='text-sm text-muted-foreground'>
              Credits never expire. Use them whenever you need.
            </p>
          </div>
          <button
            type='button'
            onClick={onClose}
            className='p-1 rounded hover:bg-muted transition-colors'
            aria-label='Close'
          >
            <X size={18} className='text-muted-foreground' />
          </button>
        </div>

        <div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
          {PACKS.map(pack => (
            <div
              key={pack.product}
              className={`p-5 border text-center flex flex-col transition-all ${
                pack.hero
                  ? 'border-growth bg-growth/5 shadow-lg relative'
                  : 'border-border bg-card/50'
              }`}
            >
              {pack.hero && (
                <div className='absolute -top-2.5 left-1/2 -translate-x-1/2 bg-growth text-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded'>
                  Best Value
                </div>
              )}
              <div className='text-[10px] uppercase tracking-wider text-muted-foreground mb-1'>
                {pack.label}
              </div>
              <div className='text-3xl font-bold text-foreground mb-1'>
                {pack.count}
              </div>
              <div className='text-xs text-muted-foreground mb-3'>
                {pack.count === 1 ? 'audit' : 'audits'}
              </div>
              <div className='text-2xl font-bold text-growth mb-1'>
                {pack.price}
              </div>
              <div className='text-xs text-muted-foreground mb-3'>
                {pack.perAudit}/audit
              </div>
              {pack.save && (
                <div className='mb-3 inline-block px-2 py-0.5 bg-growth/10 text-growth text-xs font-semibold self-center'>
                  {pack.save}
                </div>
              )}
              <Button
                type='button'
                onClick={() => {
                  void openCheckout(pack.product, 'pack_modal')
                  onClose()
                }}
                variant={pack.hero ? 'default' : 'outline'}
                size='sm'
                className='mt-auto w-full'
              >
                Buy {pack.label}
              </Button>
            </div>
          ))}
        </div>

        <div className='flex flex-col items-center gap-2 mt-6'>
          <PoweredByPaddle variant='inline' />
          <p className='text-[11px] text-muted-foreground'>
            14-day refund policy.
          </p>
        </div>
      </div>
    </div>
  )
}
